import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import SettingsSvg from '../other/svg/SettingsSvg'
import View from '../atoms/View'
import Space from '../atoms/Space'
import Text from '../atoms/TextNew'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import DeleteIntegrationModal from '../molecules/DeleteIntegrationModal'
import { RootReducer } from 'src/redux/rootReducer'
import {
  CREATE_CHANNEL_ACTIVATION,
  DELETE_CHANNEL_ACTIVATION,
  FETCH_CHANNELS,
  SYNC_CHANNEL_ACTIVATION,
  Channel
} from 'src/redux/integrationsReducer'

interface FormValues {
  login: string
  password: string
}

const inputStyle = { borderRadius: '12px', border: '1px solid #bebebe', maxWidth: '420px' }

const Integrations = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const channels = useSelector((state: RootReducer) => state.integrationsReducer.channels)
  const [addingChannelId, setAddingChannelId] = useState<number | null>(null)

  useEffect(() => {
    dispatch(FETCH_CHANNELS())
  }, [dispatch])

  useEffect(() => {
    if (addingChannelId === null) {
      return
    }
    const added = channels.find((channel) => channel.id === addingChannelId)
    if ((added?.activation ?? null) !== null) {
      setAddingChannelId(null)
    }
  }, [channels, addingChannelId])

  const connected = channels.filter((channel) => channel.activation !== null)
  const available = channels.filter((channel) => channel.activation === null)
  const [firstAvailable] = available
  const channelBeingAdded = available.find((channel) => channel.id === addingChannelId) ?? null

  return (
    <Layout
      iconComponent={<SettingsSvg className="w-8 h-8 text-white" />}
      withFiler={false}
      name={t('integrations_screen.title')}>
      <View className="bg-lightGrey shadow-bothSmall rounded-3">
        <View className="mx-10">
          <Space y={8} />
          <View className="flex items-center justify-between">
            <Text className="ml-4 text-xl font-SemiBold">{t('integrations').toUpperCase()}</Text>
            {available.length > 0 && addingChannelId === null && (
              <Button
                onClick={() => {
                  setAddingChannelId(firstAvailable.id)
                }}
                dataTestId="add-integration">
                {t('integrations_screen.add')}
              </Button>
            )}
          </View>
          <Space y={4} />
          {connected.length === 0 && addingChannelId === null && (
            <Text className="ml-4 text-base">{t('integrations_screen.empty')}</Text>
          )}
          {connected.map((channel) => (
            <ConnectedChannel key={channel.id} channel={channel} />
          ))}
          {channelBeingAdded !== null && (
            <View className="bg-white rounded-3 p-6 mb-6">
              <ChannelHeading channel={channelBeingAdded} />
              <Space y={4} />
              <CredentialForm
                channelId={channelBeingAdded.id}
                onDone={() => {
                  setAddingChannelId(null)
                }}
              />
            </View>
          )}
          <Space y={8} />
        </View>
      </View>
    </Layout>
  )
}

const ChannelHeading = ({ channel }: { channel: Channel }): JSX.Element => {
  return (
    <View className="flex items-center">
      {channel.logo_url !== null && channel.logo_url !== '' && (
        <img src={channel.logo_url} alt={channel.name} className="h-8 mr-4" />
      )}
      <Text className="text-xl font-SemiBold">{channel.name}</Text>
    </View>
  )
}

const ConnectedChannel = ({ channel }: { channel: Channel }): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { activation } = channel
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)

  if (activation === null) {
    return <View />
  }

  return (
    <View className="bg-white rounded-3 p-6 mb-6">
      <ChannelHeading channel={channel} />
      <Space y={4} />
      <Text className="text-base">{t('integrations_screen.connected')}</Text>
      <Space y={2} />
      <Text className="text-base">{`${t('integrations_screen.seller_id')}: ${activation.mobile_seller_id ?? ''}`}</Text>
      <Space y={4} />
      <View className="flex">
        <Button
          onClick={() => dispatch(SYNC_CHANNEL_ACTIVATION({ id: activation.id }))}
          dataTestId="sync-mobile-vehicles">
          {t('integrations_screen.sync')}
        </Button>
        <View className="w-4" />
        <Button
          onClick={() => {
            setConfirmingDisconnect(true)
          }}
          color="errorRed"
          dataTestId="disconnect-integration">
          {t('integrations_screen.disconnect')}
        </Button>
      </View>
      <DeleteIntegrationModal
        isOpen={confirmingDisconnect}
        channelName={channel.name}
        onClose={() => {
          setConfirmingDisconnect(false)
        }}
        onConfirm={() => {
          dispatch(DELETE_CHANNEL_ACTIVATION({ id: activation.id }))
          setConfirmingDisconnect(false)
        }}
      />
    </View>
  )
}

interface CredentialFormProps {
  channelId: number
  onDone: () => void
}

const CredentialForm = ({ channelId, onDone }: CredentialFormProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  return (
    <Form
      onSubmit={(values: FormValues) => {
        dispatch(
          CREATE_CHANNEL_ACTIVATION({
            channel_id: channelId,
            login: values.login,
            password: values.password
          })
        )
      }}
      initialValues={{ login: '', password: '' }}
      render={({ handleSubmit }) => (
        <form
          onSubmit={(event) => {
            void handleSubmit(event)
          }}>
          <Field name="login">
            {({ input }) => (
              <Input
                onChange={input.onChange}
                value={input.value}
                label={t('integrations_screen.login')}
                dataTestId="mobile-login"
                containerStyles={inputStyle}
              />
            )}
          </Field>
          <Space y={4} />
          <Field name="password">
            {({ input }) => (
              <Input
                onChange={input.onChange}
                value={input.value}
                type="password"
                label={t('integrations_screen.password')}
                dataTestId="mobile-password"
                containerStyles={inputStyle}
              />
            )}
          </Field>
          <Space y={4} />
          <View className="flex">
            <Button
              onClick={() => {
                void handleSubmit()
              }}
              dataTestId="connect-mobile">
              {t('integrations_screen.connect')}
            </Button>
            <View className="w-4" />
            <Button onClick={onDone} color="transparent" dataTestId="cancel-integration">
              {t('integrations_screen.cancel')}
            </Button>
          </View>
        </form>
      )}
    />
  )
}

export default Integrations
